import { useEffect, useRef, useState } from 'react';

import debounce from 'lodash/debounce';

import { theme } from '@prose-ui';
import { legacyTheme, styled } from '@prose-ui/legacy';

import { Typography } from 'Components/Typography';

import { moduleTestimonials } from 'assets/content/landingPages/supplements';

import useResponsiveVariant from 'utils/useResponsiveVariant';

import { CTA } from './CTA';
import { LineSeparator } from './LineSeparator';
import { RevealedArrow } from './RevealedArrow';

const Root = styled.section`
  display: flex;
  flex-direction: column;
  width: 100vw;
  padding: 80px 24px 35px;

  background-color: ${theme.colors.secondary[100]};
  border-bottom: 2px solid ${theme.colors.primary[400]};

  ${legacyTheme.breakpoints.up('sm')} {
    padding: 118px 72px 79px;
  }

  ${legacyTheme.breakpoints.up('md')} {
    padding: 124px 135px 48px;
  }
`;

const Header = styled.div`
  margin-bottom: 50px;

  ${legacyTheme.breakpoints.up('sm')} {
    margin-bottom: 56px;
  }

  ${legacyTheme.breakpoints.up('md')} {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 111px;
  }
`;

const Description = styled.div`
  display: flex;
  flex-direction: column;

  ${legacyTheme.breakpoints.up('sm')} {
    display: block;
  }

  ${legacyTheme.breakpoints.up('md')} {
    max-width: 500px;
  }
`;

const Title = styled(Typography)`
  margin-bottom: 24px;

  font-size: 36px;
  line-height: 1.17;
  letter-spacing: 0.25px;

  ${legacyTheme.breakpoints.up('sm')} {
    margin-bottom: 32px;
    font-size: 60px;
    letter-spacing: normal;
  }

  ${legacyTheme.breakpoints.up('md')} {
    max-width: 500px;
    margin-right: 50px;
    margin-bottom: 0;
  }
`;

const Paragraph = styled(Typography)`
  margin-bottom: 0;

  line-height: 1.5;
  letter-spacing: 0.25px;

  ${legacyTheme.breakpoints.up('sm')} {
    margin-bottom: 40px;
  }

  ${legacyTheme.breakpoints.up('md')} {
    margin-bottom: 48px;
  }
`;

const Wrapper = styled.div`
  display: flex;
  margin-top: 47px;

  ${legacyTheme.breakpoints.up('sm')} {
    margin-top: 55px;
  }

  ${legacyTheme.breakpoints.up('md')} {
    display: flex;
    flex-direction: column;
    width: 100%;
    margin-top: 0;
  }
`;

const Steps = styled.div`
  margin-left: 29.5px;
  margin-bottom: 72px;

  ${legacyTheme.breakpoints.up('sm')} {
    margin-right: 11.5px;
    margin-bottom: 83px;
  }

  ${legacyTheme.breakpoints.up('md')} {
    display: flex;
    justify-content: space-around;

    margin-top: 38px;
    margin-bottom: 93px;
    margin-left: 0;
  }
`;

const Step = styled.div`
  position: relative;

  margin-bottom: 48px;

  ${legacyTheme.breakpoints.up('sm')} {
    margin-bottom: 54px;
  }

  ${legacyTheme.breakpoints.up('md')} {
    max-width: 300px;
    margin-right: 50px;
    margin-bottom: 0;
  }

  &:last-of-type {
    margin-bottom: 0;
  }
`;

const Date = styled(Typography)`
  margin-bottom: 18px;

  font-size: 24px;
  line-height: normal;
  letter-spacing: 0.86px;

  ${legacyTheme.breakpoints.up('md')} {
    margin-bottom: 16px;
    line-height: 1.33;
    letter-spacing: normal;
  }
`;

const StepDot = styled.div`
  position: absolute;
  top: 7px;
  left: -52px;

  width: 18px;
  height: 18px;

  background-color: ${theme.colors.supps[200]};
  border-radius: 50%;
  border: 2px solid ${theme.colors.primary[400]};

  ${legacyTheme.breakpoints.up('md')} {
    top: -61px;
    left: 0;
    width: 21px;
    height: 21px;
  }
`;

const Testimonial = styled(Typography)`
  margin-bottom: 8px;
  letter-spacing: normal;

  ${legacyTheme.breakpoints.up('sm')} {
    margin-bottom: 16px;
  }

  ${legacyTheme.breakpoints.up('md')} {
    margin-bottom: 24px;
    letter-spacing: 0.5px;
  }
`;

const Author = styled(Typography)`
  line-height: 1.57;
  letter-spacing: 0.25px;

  ${legacyTheme.breakpoints.up('md')} {
    margin-bottom: 24px;
    letter-spacing: 0.5px;
  }
`;

const Info = styled(Typography)`
  margin-left: 47px;

  line-height: 1.5;
  letter-spacing: 0.25px;

  ${legacyTheme.breakpoints.up('sm')} {
    margin-right: 29px;
  }

  ${legacyTheme.breakpoints.up('md')} {
    max-width: 500px;
    margin-right: auto;
    margin-left: auto;
    text-align: center;
  }

  & sup {
    font-size: 60%;
    font-weight: 500;
  }
`;

export const ModuleTestimonials = () => {
  const { isMobile, isDesktop } = useResponsiveVariant();
  const stepsBlock = useRef<HTMLDivElement | null>(null);
  const [stepsSize, setStepsSize] = useState<{ height: number; width: number } | null>(null);

  const handleScroll = () => {
    const stepsBounding = stepsBlock?.current?.getBoundingClientRect();

    if (stepsBounding) {
      setStepsSize({
        height: stepsBounding.height,
        width: stepsBounding.width,
      });
    }
  };

  useEffect(() => {
    // The debounce helps computing less (better for the JS heap size)
    window.addEventListener('resize', debounce(handleScroll, 250));

    return () => {
      window.removeEventListener('resize', handleScroll);
    };
  }, []);

  useEffect(() => {
    const stepsBounding = stepsBlock?.current?.getBoundingClientRect();

    if (stepsBounding) {
      setStepsSize({
        height: stepsBounding.height,
        width: stepsBounding.width,
      });
    }
  }, [stepsBlock]);

  return (
    <Root data-testid="supplements-landing-module">
      <Header>
        <Title markupName="p" variant="p1">
          {moduleTestimonials.title}
        </Title>

        <Description>
          <Paragraph markupName="p" variant="p1">
            {moduleTestimonials.paragraph}
          </Paragraph>

          {!isMobile && <CTA buttonVariant="whiteFull" />}
        </Description>
      </Header>

      {!isDesktop && <LineSeparator />}

      <Wrapper>
        {stepsSize?.height && stepsSize?.width && (
          <RevealedArrow height={stepsSize.height} width={stepsSize.width} />
        )}

        <Steps ref={stepsBlock}>
          {moduleTestimonials.steps.map((el) => (
            <Step key={el.date}>
              <StepDot />
              <Date markupName="p" variant="p1">
                {el.date}
              </Date>
              <Testimonial markupName="p" variant="p1">
                {el.testimonial}
              </Testimonial>
              <Author markupName="p" variant="p2">
                {el.author}
              </Author>
            </Step>
          ))}
        </Steps>
      </Wrapper>

      <Info markupName="p" variant="p3">
        {moduleTestimonials.info}
      </Info>
    </Root>
  );
};
